import type { Flows, Mood, Terrain, Units } from "@tow/shared";
import { settings } from "./config";

export interface Commander {
  id: string;
  name: string;
  color: string;
}

export interface NationRef { id: string; name: string; color: string }

export interface CommanderMarker extends Commander {
  /** Their capital city. */
  capital: string;
  lat: number;
  lng: number;
  places: number;
  nation?: NationRef | null;
}

export interface PlayerView extends Commander {
  money: number;
  oil: number;
  steel: number;
  food: number;
  /** People living in the places you lead. */
  population: number;
  /** Soldiers and vehicle crews. */
  soldiers: number;
  places: number;
  capital: { id: string; name: string; lat: number; lng: number } | null;
  /** Your nation and your role in it. */
  nation?: (NationRef & { role: Role }) | null;
  /** Your capital city fell: choose to move it or surrender, until then. */
  surrender?: SurrenderChoice | null;
  taxRate: number;
  /** 0..100 */
  happiness: number;
  /** What comes in and goes out an hour as things stand. */
  perHour: Flows;
  /** Why your people are as happy as they are. */
  mood: Mood;
  /** People gained (or lost) a day at this rate. */
  peoplePerDay: number;
  /** First steps: a farm, a barracks, trained troops, a town taken. */
  tutorial: { farm: boolean; barracks: boolean; trained: boolean; expanded: boolean };
  /** Asked: one farm for all your land. Its size, the farmland it works now, all your farmland, and when it's done growing. */
  farm?: PlayerFarm | null;
  /** Asked: one steel mill for all your land, like the farm. */
  mill?: PlayerMill | null;
  startedAt: string | null;
}

/** A building in a town. Its leader also sees what it makes an hour. */
/** The country's farm or steel mill (asked: one of each for all your land). */
export interface WorksView { km2: number; working: number; doneAt?: string; mostKm2: number; perHour: number; workers: number; staffing: number }
export type PlayerFarm = WorksView & { farmlandKm2: number };
export type PlayerMill = WorksView & { landKm2: number };

export interface BuildingInfo {
  type: string;
  /** A farm (asked: as big as your land): its land in km², and before it's done growing. */
  km2?: number;
  prevKm2?: number;
  /** The level it's being built or upgraded to. */
  level: number;
  /** The level it works at now (one less while it's upgraded; 0 while it's first built). */
  workingLevel: number;
  ready: boolean;
  doneAt: string;
  /** Food, oil or steel an hour (power plants: oil burned, below zero). */
  perHour?: number;
  staffing?: number;
  /** Knocked out by bombs until then: it makes nothing, trains nothing; an airbase's aircraft can't take off. */
  downUntil: string | null;
  /** For its leader, for buildings that need power (factories, radar, anti-air): whether a power plant reaches it. */
  powered?: boolean;
  /** Fortifications: damage from battles (0..1 of full strength) after repairs. */
  damage?: number;
  /** For its leader: engineers are repairing it, done then (Milestone 11). */
  repairDoneAt?: string;
  /** For its leader: production settings (keep the town stocked with a unit). */
  production?: { unit: string; keep: number; paused?: string; retryAt?: string };
  /** For its leader: missiles stored at a missile site, in all and by kind. */
  stock?: number;
  stocks?: Record<string, number>;
  /** Damage from strikes (Milestone 15): its state, the share of hit points lost, and when a rebuild is done. */
  state?: "intact" | "damaged" | "ruined" | "rubble";
  destruction?: number;
  rebuildDoneAt?: string;
  /** Schools and hospitals can't be hit. */
  protected?: boolean;
}

/** Strikes (Milestone 15). */
export interface StrikeAim { building?: string; point?: [number, number, number]; troops?: boolean; road?: string }
export interface StrikeOrder { weapon: string; from: string; target: string; aim: StrikeAim; rounds?: number }
export interface StrikePreview {
  weapon: string; rounds: number; from: { id: string; name: string }; target: { id: string; name: string };
  miles: number; seconds: number; cost: Record<string, number>; blastFt: number; counter: string;
  road: { to: string; bridge: boolean } | null; stopChance?: number;
}
export interface StrikeResultView {
  through: number; stopped: number; lost: Units; killed: Units; road: { to: string; state: string | null } | null;
  buildings: { type: string; before: string; state: string }[]; blocks: { destroyed: number; fell: number } | null;
}
export interface StrikeView {
  id: string; weapon: string; rounds: number; status: "flying" | "hit" | "intercepted";
  from: { id: string; name: string; lat: number; lng: number } | null; target: { id: string; name: string; lat: number; lng: number }; aim: StrikeAim;
  launchAt: string; impactAt: string; result: StrikeResultView | null; attacker?: Commander;
}

/** The income breakdown (GET /me/economy). */
export interface EconomyReport {
  perHour: Flows;
  mood: Mood;
  peoplePerDay: number;
  happiness: number;
  taxRate: number;
  places: { id: string; name: string; population: number; realPopulation: number; food: number; oil: number; steel: number; staffing: number; unrest: boolean }[];
  ledger: { day: string; taxes: number; wages: number; foodMade: number; foodEaten: number; oilMade: number; oilUsed: number; steelMade: number; people: number; tradeIn?: number; tradeOut?: number }[];
}

/** Who defends a place, as far as you may know. */
export type Defenders =
  | { kind: "militia"; count: number }
  | { kind: "garrison"; units: Record<string, number> }
  /** As your recon flight last saw it. */
  | { kind: "seen"; units: Record<string, number>; seenAt: string }
  | { kind: "unknown" };

/** A spot on a town's base grid (war update): a cell from its south-west corner, turned or not. */
export interface Spot {
  gx: number;
  gz: number;
  rot: 0 | 1;
}
/** A town's base grid: its size (cells a side), cells free, and where each building stands. */
export interface BaseGridInfo {
  side: number;
  free: number;
  placed: (Spot & { type: string; outside: boolean })[];
  /** Cells the farm's fields take, the farm's land and all the town's (asked: a farm as big as your land). */
  fields?: number;
  /** Cells the steel mill takes (asked: like the farm), and its land here. */
  mill?: number;
  millKm2?: number;
  farmKm2?: number;
  landKm2?: number;
  /** Of the land, farmland (area times farmland quality): what a farm can work (asked). */
  farmlandKm2?: number;
  /** For the capital of a held state (asked: a state becomes one town): the state it stands for. */
  state?: { key: string; name: string; towns: number; people: number };
}

export interface PlaceInfo {
  id: string;
  /** Asked: a held state becomes one town. The state this town is part of, and its capital. */
  mergedInto?: { key: string; name: string; seat: { id: string; name: string } } | null;
  name: string;
  kind: string | null;
  county: string | null;
  state: { id: string; name: string } | null;
  country: string;
  countryCode: string;
  population: number;
  realPopulation: number;
  areaKm2: number;
  lat: number;
  lng: number;
  terrain: Terrain;
  elevation: number;
  isCoastal: boolean;
  farmlandQuality: number;
  /** Farmland in km2 of the best land: what farms can work. */
  farmlandKm2: number;
  /** 0..1 oil and gas under it from real fields, and the field's name. */
  oil: number;
  oilField: string | null;
  forestShare: number;
  leader: Commander | null;
  /** Its leader's nation. */
  nation?: NationRef | null;
  isCapital: boolean;
  defenders: Defenders;
  /** Its troops' strength and supply (Milestone 11): only its leader sees this. */
  supply?: { strength: number; supplied: boolean; noAmmo: boolean; noAmmoSince: string | null } | null;
  startRefusal: "has_leader" | "too_big" | null;
  /** The army a new leader would get here (as many soldiers as the town's taxes can pay). */
  startingArmy: Units;
  neighbours: { id: string; name: string; kind: "land" | "coast"; distanceKm: number; leader: Commander | null }[];
  /** What's built there (anyone can see buildings). */
  buildings: BuildingInfo[];
  /** What's being trained or built there, and where (barracks, factory, airbase, port): only its leader sees this. */
  training: { at: string; unit: string; count: number; doneAt: string }[];
  /** War update: the base grid. */
  grid?: BaseGridInfo;
  /** Its state (asked): who controls it, and how much of it you hold (share of its people). */
  /** States (asked): who controls the town's state (a nation, or a leader in no nation) and your side's share of all its people. */
  stateControl?: {
    key: string; name: string; needed: number;
    /** Winning a state makes every town in it the winner's, and its troops are one state army (asked). */
    whole?: boolean;
    controller: (StateController & { share: number; yours: boolean }) | null; mine: number | null;
    /** Your state army there: kept in your biggest town of it (only you see it). */
    army?: { seat: { id: string; name: string }; units: Units } | null;
    /** Your capital of it (your biggest town there), if your side controls it: where a State Farm goes. */
    seat?: string | null;
  } | null;
  /** War update: taken in a war and held until a peace treaty says who keeps it. */
  occupied?: { from: Commander } | null;
  /** War update, for its leader: its borders, the share of troops posted on each, and the reserve. */
  fronts?: { id: string; name: string; kind: "land" | "sea"; side?: "yours" | "leader" | "free" | "sea"; leader?: Commander | null; share: number; posted: Record<string, number> }[] | null;
  reserve?: Record<string, number> | null;
  /** Its defenders are tied up from a battle over that border until then. */
  engaged?: { front: string; until: string } | null;
  /** The weather there now, then each hour of the forecast. */
  weather?: WeatherHour[];
}

export type WeatherType = "clear" | "rain" | "storm" | "snow" | "blizzard" | "sandstorm";
export interface WeatherHour { at: string; type: WeatherType }
/** A weather system on the map: how far its weather reaches, and its stormy heart (km). */
export interface WeatherSystemView { id: string; lat: number; lng: number; outerKm: number; coreKm: number; outer: WeatherType; core: WeatherType | null }
/** The world's weather now and for each hour of the forecast: the same for every player. */
export interface WeatherMap { frames: { at: string; systems: WeatherSystemView[] }[] }

/** A stop on an army's route, with when the army gets there. */
/** A stop on a route; `border`: where it crosses into this town from the stop before ([lng, lat]). */
export interface PathPoint { id: string; name: string; lat: number; lng: number; at: string; border?: [number, number] }

/** One of your armies on the move. */
export interface ArmyView {
  id: string;
  units: Units;
  path: PathPoint[];
  from: string;
  to: string;
  departAt: string;
  arriveAt: string;
  status: "marching" | "returning" | "fighting";
  /** War update: it's attacking: it stops at the town's border, where the battle is. */
  attack?: boolean;
}

/** An enemy army your troops can see coming: its last leg only. */
export interface SeenArmy {
  id: string;
  owner: Commander;
  units: Units;
  path: PathPoint[];
  to: string;
  arriveAt: string;
}

export interface RouteView {
  path: PathPoint[];
  /** Game seconds on the march. */
  seconds: number;
  /** Oil it burns, paid when it sets out. */
  fuel: number;
  /** It sails. */
  sea: boolean;
  /** Where bad weather slows it on the way. */
  weather?: { place: string; type: WeatherType }[];
  /** Vehicles set out with no fuel depot in range: half speed (Milestone 11). */
  noFuel?: boolean;
  /** What attacking the destination would be: a surprise attack, open war, a civil war... */
  attack?: { kind: AttackKind; target: SideRef | null };
  /** War update: the border it comes over (a neighbouring town, or the coast). */
  front?: { id: string; name: string } | null;
  /** Your other armies on their way there, and when the last arrives. */
  others?: { count: number; lastArrival: string } | null;
}

/** Where something is when: [lng, lat, ms]. */
export type Track = [number, number, number][];

export type Mission = "strike" | "recon" | "rebase";

/** One of your air missions. */
export interface FlightView {
  id: string;
  mission: Mission;
  units: Units;
  base: { id: string; name: string };
  /** For a strike, `what` is "troops" or the building it aims at. */
  target: { id: string; name: string; what: string | null };
  track: Track;
  departAt: string;
  impactAt: string;
  returnAt: string;
  status: "outbound" | "returning";
}

/** Another leader's aircraft on your radar: only the part of its flight your radar sees. */
export interface RadarContact {
  id: string;
  owner: Commander;
  units: Units;
  track: Track;
  /** Your town it's heading for, when it's one of yours. */
  target: { id: string; name: string; impactAt: string } | null;
  seenUntil: string;
}

export interface FlightPreview {
  mission: Mission;
  km: number;
  rangeMiles: number;
  /** Game seconds to the target, and for the whole flight. */
  seconds: number;
  totalSeconds: number;
  fuel: number;
  track: Track;
  /** The weather over the target when they get there. */
  weather?: WeatherType;
}

/** A mission's report: what happened at the target. */
export interface FlightReport {
  id: string;
  mission: Mission;
  owner: Commander;
  target: { id: string; name: string; what: string | null };
  base?: { id: string; name: string };
  fuel?: number;
  impactAt: string;
  status: string;
  seed: number;
  result: null | {
    dogfight: { strike: Units; interceptors: Units }[];
    shotDown: Units;
    antiAir: number;
    survivors: Units;
    hits: number;
    repairMinutes: number;
    killed: Units | null;
    photographed: string[];
    /** The weather over the target. */
    weather?: WeatherType;
  };
}

/** Your airbases and radars. */
export interface Forces {
  airbases: { id: string; name: string; lat: number; lng: number; level: number; room: number; aircraft: Units; groundedUntil: string | null; helipad?: boolean }[];
  radars: { id: string; name: string; lat: number; lng: number; working: boolean; powered: boolean; rangeMiles: number }[];
}

/** One thing that affected a battle: which side, which units (none: all of them), and by how much. */
export interface BattleNote {
  key: string;
  side: "attack" | "defense";
  units: string[];
  multiplier: number;
}

export interface BattleReport {
  id: string;
  place: { id: string; name: string; terrain?: Terrain; coastal?: boolean };
  attacker: Commander;
  /** Empty when a town's militia fought. */
  defender: Commander | null;
  attackers: Units;
  defenders: Units;
  situation: {
    terrain: Terrain; capital: boolean; population: number; attackerWithoutCapital: boolean; defenderWithoutCapital: boolean;
    attackerStarving?: boolean; defenderStarving?: boolean; weather?: string; acrossRiver?: boolean; fortification?: number; coastal?: boolean;
    /** The town's fortifications (Milestone 10): levels, strength at the start, and at full repair. */
    works?: { levels: Record<string, number>; hp: number; maxHp: number };
  };
  rounds: { attackers: Units; defenders: Units; works?: number }[];
  winner: "attacker" | "defender";
  seed: number;
  foughtAt: string;
  /** How many of the attacker's armies fought together. */
  armies?: number;
  modifiers?: { attack: Record<string, number>; defense: number; notes: BattleNote[] };
  /** The server fought it again from its seed and got exactly the same rounds. */
  replayMatches?: boolean | null;
  /** Fought live in a zone (Milestone 13). */
  live?: boolean;
  /** What the map showed of it (asked: the replay is the real fight); battles before Oct 21, 2026 don't have it. */
  view?: import("./live").BattleEvent;
}

export interface Notice {
  id: string;
  kind: string;
  message: string;
  battle?: string;
  place?: string;
  /** An air mission's report. */
  flight?: string;
  at: string;
  read: boolean;
}

/** A town you lead, with the troops in it. */
export interface MyPlace {
  id: string;
  name: string;
  lat: number;
  lng: number;
  population: number;
  garrison: Units;
  isCapital: boolean;
  isCoastal: boolean;
  /** Supply (Milestone 11). */
  strength?: number;
  supplied?: boolean;
  noAmmo?: boolean;
}

export interface PlaceMatch {
  id: string;
  name: string;
  kind: string | null;
  county: string | null;
  state: string | null;
  country: string;
  population: number;
  lat: number;
  lng: number;
  leader: Commander | null;
}

export interface LedInView {
  leaders: Record<string, { name: string; color: string; nation?: NationRef | null }>;
  /** [place id, leader id] */
  places: [string, string][];
  truncated: boolean;
}

/** A refusal from the world server, with the message to show the player. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

export interface TerritoryInfo {
  id: string;
  name: string;
  country: string;
  terrain: Terrain;
  elevation: number;
  is_coastal: boolean;
  area_km2: number;
  farmland_quality: number;
  forest_share: number;
  fresh_water: boolean;
  lat: number;
  lng: number;
  neighbors: { id: string; name: string; kind: "land" | "coast" | "sea"; crosses_river: boolean; distance_km: number }[];
}

const TOKEN_KEY = "tow.session";

/** The session token (in this browser only). Storage can be unavailable; then you stay signed out. */
export const session = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string | null) {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* private mode: session lasts until the tab closes */
    }
  },
};

async function request<T>(method: "GET" | "POST", path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const token = session.get();
  const res = await fetch(`${settings.worldServer}${path}`, {
    method,
    signal,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? "error", data.message ?? `The world server said ${res.status}.`);
  return data as T;
}

const get = <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal);
const post = <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {});

export const signUp = (name: string, color: string) => post<{ token: string; accountKey: string; player: PlayerView }>("/auth/guest", { name, color });
export const signInWithKey = (accountKey: string) => post<{ token: string; player: PlayerView }>("/auth/key", { accountKey });
export const fetchMe = () => get<PlayerView>("/me");
/** A one-time code to sign in to the 3D game with (Milestone 12). */
export const gameCode = () => post<{ code: string; expiresAt: string }>("/auth/link", {});
export const startCountry = (place: string) => post<PlayerView>("/me/start", { place });
export const reportCommander = (commander: string, reason: string, details?: string) => post<{ ok: true }>("/reports", { commander, reason, details });
export const fetchMyPlaces = (signal?: AbortSignal) => get<{ places: MyPlace[] }>("/me/places", signal);
export const setCapital = (place: string) => post<{ ok: true }>("/me/capital", { place });

export const buildAt = (place: string, type: string, at?: Spot) => post<{ id: string; type: string; level: number; doneAt: string }>(`/places/${encodeURIComponent(place)}/build`, { type, ...(at ? { at } : {}) });
/** States (asked): every controlled state, and each leader's realm (their towns as one shape). */
/** Who controls a state: a nation (its members together), or a leader in no nation. */
export interface StateController { kind: "nation" | "leader"; id: string; name: string; color: string }
export interface ControlledState {
  key: string; name: string; country: string; controller: StateController; share: number; since: string; territories: string[];
  /** Asked: a held state becomes one town. Its capital: labelled on the map, and what opens when any of its towns is clicked. */
  seat?: { id: string; name: string; lng: number; lat: number; people: number };
}
export const fetchStates = () => get<{ states: ControlledState[]; needed: number }>("/states");
export const fetchRealms = () => get<GeoJSON.FeatureCollection>("/realms");
/** War update: posts shares of a town's troops on its borders (the rest stay in reserve). */
export const setDeployment = (place: string, shares: Record<string, number>) =>
  post<{ deployment: Record<string, number> }>(`/places/${encodeURIComponent(place)}/deployment`, { shares });
/** War update: towns occupied between you and another side, and how many a peace treaty can keep. */
export const fetchOccupation = (side: string) =>
  get<{ mine: { id: string; name: string }[]; theirs: { id: string; name: string }[]; keepMine: number; keepTheirs: number }>(`/diplomacy/occupation?with=${encodeURIComponent(side)}`);
/** War update: moves or turns a building on the town's base grid. */
export const moveAt = (place: string, type: string, at: Spot) => post<BaseGridInfo>(`/places/${encodeURIComponent(place)}/move`, { type, at });
/** Asked: one farm for all your land. Makes it this many km² of your farmland. */
export const setMyFarm = (km2: number) => post<{ km2: number; doneAt: string }>("/me/farm", { km2 });
/** Asked: one steel mill for all your land. Makes it this many km² of your land. */
export const setMyMill = (km2: number) => post<{ km2: number; doneAt: string }>("/me/mill", { km2 });
export const upgradeAt = (place: string, type: string) => post<{ id: string; type: string; level: number; doneAt: string }>(`/places/${encodeURIComponent(place)}/upgrade`, { type });
export const repairAt = (place: string, type: string) => post<{ doneAt: string }>(`/places/${encodeURIComponent(place)}/repair`, { type });
export const rebuildAt = (place: string, type: string) => post<{ doneAt: string; cost: Record<string, number> }>(`/places/${encodeURIComponent(place)}/rebuild`, { type });
export const previewStrike = (o: StrikeOrder) => post<StrikePreview>("/strikes/preview", o);
export const launchStrike = (o: StrikeOrder) => post<{ id: string; impactAt: string; rounds: number }>("/strikes", o);
export const fetchStrikes = (signal?: AbortSignal) => get<{ mine: StrikeView[]; incoming: StrikeView[] }>("/strikes", signal);
export const setTaxRate = (rate: number) => post<{ ok: true; taxRate: number }>("/me/tax", { rate });
export const disbandAt = (place: string, unit: string, count: number) => post<{ garrison: Units }>(`/places/${encodeURIComponent(place)}/disband`, { unit, count });
export const fetchEconomy = () => get<EconomyReport>("/me/economy");
export const trainAt = (place: string, unit: string, count: number) =>
  post<{ id: string; unit: string; count: number; doneAt: string }>(`/places/${encodeURIComponent(place)}/train`, { unit, count });

export const previewMarch = (from: string, to: string, units: Units) => post<RouteView>("/armies/preview", { from, to, units });
export const march = (from: string, to: string, units: Units, together = false, stateStrike = false) =>
  post<ArmyView>("/armies", { from, to, units, ...(together ? { together } : {}), ...(stateStrike ? { stateStrike } : {}) });
export const recallArmy = (id: string) => post<ArmyView>(`/armies/${encodeURIComponent(id)}/recall`);
export const fetchArmies = () => get<{ mine: ArmyView[]; seen: SeenArmy[] }>("/armies");
export const fetchBattle = (id: string, signal?: AbortSignal) => get<BattleReport>(`/battles/${encodeURIComponent(id)}`, signal);
export const fetchNotices = () => get<{ unread: number; items: Notice[] }>("/notifications");

export interface FlightOrder { from: string; to: string; units: Units; mission: Mission; target?: string }
export const previewFlight = (o: FlightOrder) => post<FlightPreview>("/flights/preview", o);
export const launchFlight = (o: FlightOrder) => post<FlightView>("/flights", o);
export const fetchFlights = () => get<{ mine: FlightView[]; seen: RadarContact[] }>("/flights");
export const fetchFlightReport = (id: string, signal?: AbortSignal) => get<FlightReport>(`/flights/${encodeURIComponent(id)}`, signal);
export const fetchForces = (signal?: AbortSignal) => get<Forces>("/me/forces", signal);
export const fetchWeather = (signal?: AbortSignal) => get<WeatherMap>("/weather", signal);
export const setProduction = (place: string, at: string, unit: string, keep: number) =>
  post<{ ok: true }>(`/places/${encodeURIComponent(place)}/production`, { at, unit, keep });
export const markNoticesRead = () => post<{ ok: true }>("/notifications/read");

// Trade (Milestone 8)
export type Good = "oil" | "steel" | "food";
export interface MarketGood {
  good: Good;
  /** What buyers pay and sellers get for one unit now (the fee included), and the price in between. */
  buy: number;
  sell: number;
  price: number;
  basePrice: number;
  stock: number;
  normalStock: number;
  maxBuy: number;
  /** The price at the end of each hour with trading, over the last day. */
  history: { at: string; price: number }[];
}
export interface MarketView { fee: number; goods: MarketGood[] }
export type RouteState = "working" | "building" | "bombed" | "blockaded" | "no_border";
export interface TradeRoute { placeId: string; placeName: string; type: "port" | "borderCrossing"; level: number; state: RouteState; capacity: number; used: number; downUntil: string | null }
export interface MarketQuote {
  good: Good; side: "buy" | "sell"; amount: number; total: number; price: number; fee: number; priceAfter: number;
  route: TradeRoute | null;
  /** Why it can't go through now (no route, routes cut or full). */
  problem: string | null;
}
export interface MarketOrderView { id: string; good: Good; side: "buy" | "sell"; amount: number; price: number; total: number; at: string; route: { type: string; placeName: string } }
export type Bundle = Partial<Record<"money" | Good, number>>;
export interface TradeOfferView {
  id: string; from: Commander; to: Commander; gives: Bundle; wants: Bundle;
  status: "open" | "accepted" | "declined" | "cancelled" | "expired" | "cut";
  fromRoute: { placeId: string; placeName: string; type: string } | null;
  toRoute: { placeId: string; placeName: string; type: string } | null;
  createdAt: string; expiresAt: string; closedAt: string | null;
}
export type Metric = "gdp" | "population" | "military" | "trade" | "land";
export interface RankingRow { rank: number; value: number; extra?: { areaKm2?: number }; player: Commander }
export interface Rankings { metric: Metric; period: string; total: number; top: RankingRow[]; me: RankingRow | null }

export const fetchMarket = (signal?: AbortSignal) => get<MarketView>("/market", signal);
export const quoteOrder = (good: Good, side: "buy" | "sell", amount: number) => post<MarketQuote>("/market/quote", { good, side, amount });
export const placeOrder = (good: Good, side: "buy" | "sell", amount: number, limit?: number) =>
  post<MarketQuote & { id: string }>("/market/orders", { good, side, amount, ...(limit !== undefined ? { limit } : {}) });
export const fetchOrders = () => get<{ orders: MarketOrderView[] }>("/market/orders");
export const fetchRoutes = () => get<{ routes: TradeRoute[] }>("/trade/routes");
export const fetchOffers = () => get<{ offers: TradeOfferView[] }>("/trade/offers");
export const makeOffer = (to: string, gives: Bundle, wants: Bundle) => post<TradeOfferView>("/trade/offers", { to, gives, wants });
export const answerOffer = (id: string, how: "accept" | "decline" | "cancel") => post<TradeOfferView>(`/trade/offers/${encodeURIComponent(id)}/${how}`);
export const fetchRankings = (metric: Metric, signal?: AbortSignal) => get<Rankings>(`/rankings?metric=${metric}`, signal);

// Nations and diplomacy (Milestone 9)
export type Role = "leader" | "officer" | "governor";
export type RelationStatus = "peace" | "tension" | "war" | "ceasefire" | "allied";
export type AttackKind = "none" | "war" | "civil_war" | "ceasefire_broken" | "surprise";
export interface Flag { colors: [string, string, string]; layout: "vertical" | "horizontal" }
export interface SideRef { kind: "nation" | "player"; id: string; name: string; color: string }
export interface SurrenderChoice { id: string; conqueror: Commander; nation: (NationRef & { flag: Flag }) | null; place: { id: string; name: string }; expiresAt: string }
export interface HistoryItem { kind: string; at: string; [k: string]: unknown }
export interface NationSummary { id: string; name: string; color: string; flag: Flag; leader: Commander; members: number; places: number; population: number }
export interface NationView extends NationRef {
  flag: Flag;
  members: { player: Commander; role: Role; via: string; joinedAt: string; places: number; population: number }[];
  myRole: Role | null;
  requests: { id: string; player: Commander; message: string | null; at: string }[] | null;
  myRequest: string | null;
  history: HistoryItem[];
}
export type TreatyType = "non_aggression" | "trade" | "military_access" | "defensive_alliance" | "full_alliance" | "ceasefire" | "peace" | "border";
export interface TreatyView {
  id: string; type: TreatyType; name: string; a: SideRef; b: SideRef; proposedBy: Commander;
  status: "proposed" | "active" | "ended" | "broken" | "declined" | "withdrawn";
  clauses: { days?: number; transfers?: { from: "a" | "b"; places: { id: string; name: string }[] }[]; payment?: { from: "a" | "b"; money: number }; keeps?: { id: string; name: string }[] };
  createdAt: string; deliveredAt: string; signedAt: string | null; endsAt: string | null; cancelAt: string | null; closedAt: string | null;
}
export interface DiplomacyView {
  side: SideRef; canAct: boolean; role: Role | null;
  relations: { with: SideRef; status: RelationStatus; since: string; reason: string | null; endsAt: string | null }[];
  treaties: TreatyView[];
  envoys: { id: string; from: Commander; to: Commander; message: string | null; treaty: string | null; sentAt: string; arriveAt: string; arrived: boolean }[];
  blocs: string[];
  surrender: SurrenderChoice | null;
}
export interface Profile {
  id: string; name: string; color: string; started: boolean; places: number; population: number;
  capital: { id: string; name: string } | null;
  nation: (NationRef & { flag: Flag; role: Role }) | null;
  side: SideRef;
  you: { status: RelationStatus | "nation"; sameNation: boolean; reason: string | null; since: string | null; treaties: TreatyType[] } | null;
  towns: { id: string; name: string }[];
  history: HistoryItem[];
}
export interface Bloc { id: string; name: string; flag: Flag; charter: string; createdAt: string; members: SideRef[]; applications: { id: string; side: SideRef; votes: Record<string, boolean> }[] }
export interface CouncilProposal {
  id: string; kind: "sanction" | "protect" | "condemn"; text: string; what: string; status: "open" | "passed" | "failed";
  by: SideRef; target: SideRef | null; place: { id: string; name: string } | null; closesAt: string; effectUntil: string | null;
  votes: { nation: { id: string; name: string }; yes: boolean; weight: number }[];
}
export interface ChatMessage { id: string; text: string; at: string; player: Commander }

export const fetchNations = () => get<{ nations: NationSummary[] }>("/nations");
export const fetchNation = (id: string, signal?: AbortSignal) => get<NationView>(`/nations/${encodeURIComponent(id)}`, signal);
export const foundNation = (name: string, color: string, flag: Flag) => post<NationRef>("/nations", { name, color, flag });
export const askToJoin = (id: string, message?: string) => post<{ ok: true }>(`/nations/${encodeURIComponent(id)}/requests`, message ? { message } : {});
export const answerRequest = (id: string, accept: boolean) => post<{ ok: true }>(`/nations/requests/${encodeURIComponent(id)}/${accept ? "accept" : "decline"}`);
export const setRole = (player: string, role: "officer" | "governor") => post<{ ok: true }>(`/nations/members/${encodeURIComponent(player)}/role`, { role });
export const handOver = (player: string) => post<{ ok: true }>(`/nations/members/${encodeURIComponent(player)}/leader`);
export const removeMember = (player: string) => post<{ ok: true }>(`/nations/members/${encodeURIComponent(player)}/remove`);
export const leaveNation = () => post<{ ok: true }>("/nations/leave");
export const surrender = (id: string) => post<{ ok: true }>(`/surrenders/${encodeURIComponent(id)}/accept`);
export const fetchChat = (channel: string) => get<{ messages: ChatMessage[] }>(`/chat/${encodeURIComponent(channel)}`);
export const sendChat = (channel: string, text: string) => post<ChatMessage>(`/chat/${encodeURIComponent(channel)}`, { text });
export const fetchDiplomacy = () => get<DiplomacyView>("/diplomacy");
export const fetchProfile = (id: string, signal?: AbortSignal) => get<Profile>(`/commanders/${encodeURIComponent(id)}`, signal);
export const declareWar = (target: string, reason: string) => post<{ ok: true }>("/diplomacy/war", { target, reason });
export const issueWarning = (target: string, reason: string) => post<{ ok: true }>("/diplomacy/warn", { target, reason });
export const proposeTreaty = (target: string, type: TreatyType, clauses: Record<string, unknown>, message?: string) =>
  post<TreatyView>("/treaties", { target, type, clauses, ...(message ? { message } : {}) });
export const signTreaty = (id: string) => post<TreatyView>(`/treaties/${encodeURIComponent(id)}/sign`);
export const refuseTreaty = (id: string) => post<{ ok: true }>(`/treaties/${encodeURIComponent(id)}/refuse`);
export const cancelTreaty = (id: string, now: boolean) => post<{ ok: true; endsAt: string | null }>(`/treaties/${encodeURIComponent(id)}/cancel`, { now });
export const sendEnvoy = (to: string, message: string) => post<{ arriveAt: string }>("/envoys", { to, message });
export const fetchBlocs = () => get<{ alliances: Bloc[] }>("/alliances");
export const createBloc = (name: string, flag: Flag, charter: string) => post<{ id: string }>("/alliances", { name, flag, charter });
export const applyToBloc = (id: string) => post<{ ok: true }>(`/alliances/${encodeURIComponent(id)}/apply`);
export const leaveBloc = (id: string) => post<{ ok: true }>(`/alliances/${encodeURIComponent(id)}/leave`);
export const voteBloc = (id: string, yes: boolean) => post<{ status: string }>(`/alliances/applications/${encodeURIComponent(id)}/vote`, { yes });
export const fetchCouncil = () => get<{ proposals: CouncilProposal[] }>("/council");
export const proposeCouncil = (kind: string, target: string | undefined, text: string) => post<{ id: string }>("/council", { kind, target, text });
export const voteCouncil = (id: string, yes: boolean) => post<{ ok: true }>(`/council/${encodeURIComponent(id)}/vote`, { yes });

export const fetchLed = (b: { west: number; south: number; east: number; north: number }, signal?: AbortSignal) =>
  get<LedInView>(`/places/led?bbox=${[b.west, b.south, b.east, b.north].map((n) => n.toFixed(4)).join(",")}`, signal);
export const fetchCommanders = () => get<{ commanders: CommanderMarker[] }>("/commanders");
export const fetchPlace = (id: string, signal?: AbortSignal) => get<PlaceInfo>(`/places/${encodeURIComponent(id)}`, signal);
export const findPlaces = (q: string, signal?: AbortSignal) => get<{ places: PlaceMatch[] }>(`/places/search?q=${encodeURIComponent(q)}`, signal);

/**
 * The built world map's version, which keys the territory tiles so browsers keep them until the
 * map is rebuilt. "" if there's no map yet or the server doesn't answer in time.
 */
export async function fetchMapVersion(timeoutMs = 2500): Promise<string> {
  try {
    const r = await get<{ mapVersion?: string }>("/meta", AbortSignal.timeout(timeoutMs));
    return r.mapVersion ?? "";
  } catch {
    return "";
  }
}

export const fetchTerritory = (id: string, signal?: AbortSignal) => get<TerritoryInfo>(`/territories/${encodeURIComponent(id)}`, signal);
/** A state as one (asked): its towns, people and holder; your state army in it, or where yours would strike it. */
export interface StateSummary {
  key: string; name: string; country: string; people: number; towns: number;
  controller: StateController | null; yours: boolean;
  mine: { towns: number; people: number; seat: { id: string; name: string }; army: Units; built: { id: string; name: string; buildings: { type: string; level: number }[] }[] } | null;
  /** everyone: nobody holds it but players do; all their troops in it defend together. */
  target: { id: string; name: string; theirArmy: boolean; everyone: boolean } | null;
  from: { key: string; name: string; seat: { id: string; name: string }; army: Units }[];
  /** Nobody holds it: all its free towns' militia, who meet a state army together. */
  militia: number | null;
  /** Your missiles and drones (at your working missile sites), to fire at it; null when it's yours or you're signed out. */
  missiles: Record<string, number> | null;
  /** Nobody holds it, and you and other leaders have towns in it (asked): who you can fight for it. */
  contest: {
    seat: { id: string; name: string }; target: { id: string; name: string }; army: Units;
    opponents: { id: string; name: string; color: string; towns: number; target: { id: string; name: string } }[];
  } | null;
}
export interface StateSalvo {
  state: string; held: boolean; fired: number; short: number;
  launched: { target: { id: string; name: string }; from: string; rounds: number; impactAt: string }[];
}
export const fightForState = (territory: string, units: Units) =>
  post<{ state: string; from: { id: string; name: string }; target: { id: string; name: string }; army: ArmyView }>(`/states/at/${encodeURIComponent(territory)}/fight`, { units });
export const missileState = (territory: string, weapon: string, rounds: number) =>
  post<StateSalvo>(`/states/at/${encodeURIComponent(territory)}/missiles`, { weapon, rounds });
/** Battles being fought now (asked: watchable), to show them when the map opens. */
export const fetchLiveBattles = () => get<{ battles: import("./live").BattleEvent[] }>("/battles/live");
/** Your towns and when each became yours, for the timelapse ([lng, lat, people, ms]). */
export const fetchTimeline = () => get<{ towns: [number, number, number, number][] }>("/me/timeline");
// Every list is there even if the server leaves one out (an older server mid-update broke the panel once).
export const fetchStateAt = (territory: string, signal?: AbortSignal) => get<StateSummary>(`/states/at/${encodeURIComponent(territory)}`, signal)
  .then((s) => ({ ...s, from: s.from ?? [], target: s.target ?? null, mine: s.mine ?? null, militia: s.militia ?? null, missiles: s.missiles ?? null, contest: s.contest ?? null }));

export interface Place {
  name: string;
  detail: string;
  lng: number;
  lat: number;
  /** [minLng, maxLat, maxLng, minLat] as Photon returns it, when the place is an area. */
  extent?: [number, number, number, number];
}

/** Free place search (Photon, OpenStreetMap data). Used only to move the map. */
export async function searchPlaces(q: string, signal?: AbortSignal): Promise<Place[]> {
  const url = `${settings.geocoder}?q=${encodeURIComponent(q)}&limit=6&lang=en`;
  const res = await fetch(url, { signal });
  if (!res.ok) return [];
  const body = (await res.json()) as { features: { geometry: { coordinates: [number, number] }; properties: Record<string, string> & { extent?: [number, number, number, number] } }[] };
  return body.features.map((f) => {
    const p = f.properties;
    const name = p.name ?? [p.housenumber, p.street].filter(Boolean).join(" ") ?? "Place";
    const detail = [p.city, p.state, p.country].filter((x) => x && x !== name).join(", ");
    return { name: name || detail, detail, lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], extent: p.extent };
  });
}

/** A town's player-built blocks (Milestone 14): which chunks it has, and how many blocks. */
export interface BlockIndex {
  chunkSize: number;
  blocks: number;
  chunks: { key: string; version: number; count: number }[];
}
export interface BlockOpView { x: number; y: number; z: number; m: string | null }
export const fetchBlockIndex = (place: string) => get<BlockIndex>(`/places/${encodeURIComponent(place)}/blocks`);
export const fetchChunks = (place: string, keys: string[]) =>
  get<{ chunks: { key: string; version: number; data: string }[] }>(`/places/${encodeURIComponent(place)}/chunks?keys=${keys.join(";")}`);
export const postBlocks = (place: string, ops: BlockOpView[]) =>
  post<{ placed: number; removed: number; paid: Record<string, number>; refunded: Record<string, number>; chunks: { key: string; version: number }[]; total: number }>(
    `/places/${encodeURIComponent(place)}/blocks`, { ops });
